import { createDefaultState, State } from './budgetCalculatorModel'
import { createDefaultSalesGoals, SalesGoal } from './salesModel'
import { uniqueId } from '../../helpers/uniqueId'
import { createDefaultEmployees, Employees, emptyHourlyEmployee, emptySalaryEmployee } from './laborModel'
import { createDefaultMaterials, Material } from './materialsModel'
import { createDefaultSubcontractors, Subcontractor } from './subcontractorModel'
import { createDefaultOverhead, emptyOverhead, OverheadGroup } from './overheadModel'
import { createDefaultEquipment, EquipmentList, LeasedEquipment, MiscEquipment, OwnedEquipment } from './equipmentModel'
import { createDefaultSeason, createDefaultMarkup, OverheadRecoveryMarkup, Season, createDefaultProfitData } from './budgetModel'

export type ImportSuccess = { state: State }
export type ImportError = { error: string }

export type ImportResult =
  | ImportSuccess
  | ImportError

type DubiousNumber = number | string
type DubiousDate = number | string

type ImportHourlyEmployee = {
  Role: string
  Name: string
  'Hourly wage': DubiousNumber
  'Total hours worked per year': DubiousNumber
  'Unbillable hours per year': DubiousNumber
  'Overtime hours': DubiousNumber
  'Overtime multiplier': DubiousNumber
  Bonuses: DubiousNumber
}

type ImportSalaryEmployee = {
  'Role': string
  'Name': string
  'Annual salary': DubiousNumber
  'Total hours worked per year': DubiousNumber
  'Unbillable hours per year': DubiousNumber
  'Overtime hours': DubiousNumber
  'Bonuses': DubiousNumber
}

type ImportEquipment = {
  Owned_Overhead: {
    Name: string
    Quantity: DubiousNumber
    'Purchase price': DubiousNumber
    'Years used': DubiousNumber
    'Sell price': DubiousNumber
  }[]
  Owned_Hourly: {
    Name: string
    Quantity: DubiousNumber
    'Purchase price': DubiousNumber
    'Years used': DubiousNumber
    'Sell price': DubiousNumber
    'Total Billable hours per year': DubiousNumber
  }[]
  Leased_Overhead: {
    Name: string
    Quantity: DubiousNumber
    'Monthly payment': DubiousNumber
  }[]
  Leased_Hourly: {
    Name: string
    Quantity: DubiousNumber
    'Monthly payment': DubiousNumber
    'Annual cost to own': DubiousNumber
    'Total Billable hours per year': DubiousNumber
  }[]
  Misc_Tools: { Name: string, 'Total Annual cost': DubiousNumber }[]
}

type ImportBudget = {
  startDate: DubiousDate
  endDate: DubiousDate
  daysInWeek: DubiousNumber
  materialRecovery: DubiousNumber
  equipmentRecovery: DubiousNumber
  subcontractorRecovery: DubiousNumber
  profitMargin: DubiousNumber
}

type ImportFormat = {
  sales?: { name: string, value?: DubiousNumber }[],
  labor?: { hourly_employees: ImportHourlyEmployee[], salary_employees: ImportSalaryEmployee[] },
  materials?: { name: string, value?: DubiousNumber }[],
  subcontractor?: { name: string, value?: DubiousNumber }[],
  overhead?: { name: string, value?: DubiousNumber, heading?: boolean }[],
  equipment?: ImportEquipment,
  budget?: ImportBudget
}

export const importBudget = (input: string): ImportResult => {
  const withoutTrailingAsterisk = input.trim().replace(/\*$/, '')
  try {
    const data = JSON.parse(withoutTrailingAsterisk)
    const defaultProfitData = createDefaultProfitData()
    return {
      state: {
        ...createDefaultState(),
        salesGoals: importSalesGoals(data),
        employees: importEmployees(data),
        materials: importMaterials(data),
        subcontractors: importSubcontractors(data),
        overheadGroups: importOverhead(data),
        equipmentList: importEquipment(data),
        profit: {
          ...defaultProfitData,
          desiredNetProfitMargin: parseProfitMargin(data.budget?.profitMargin, defaultProfitData.desiredNetProfitMargin),
          season: importSeason(data),
          overheadRecoveryMarkup: importMarkup(data),
        }
      }
    }
  }
  catch {
    return { error: 'There was a problem processing the data.' }
  }
}

const parseProfitMargin = (value: DubiousNumber, defaultValue: number): number => {
  return (parseDubiousNumber(value) / 100) || defaultValue
}

export const parseDubiousNumber = (input: DubiousNumber): number => {
  if (typeof input === 'number') {
    return input
  }

  const parsed = parseFloat(input)
  return isNaN(parsed) ? 0 : parsed
}

export const parseDubiousDate = (input: DubiousDate, defaultValue: string): string => {
  if (typeof input === 'string' && /^\d{4}[/-]\d{2}[/-]\d{2}$/.test(input)) {
    return input.replace(/\//g, '-')
  }
  return defaultValue
}

const importSalesGoals = (data: ImportFormat): SalesGoal[] => {
  if (data.sales) {
    const result = data.sales.filter(x => x.value !== undefined)
      .map(x => ({ id: uniqueId(), description: x.name, annualRevenue: parseDubiousNumber(x.value!) }))
    if (result.length > 0) {
      return result
    }
  }
  return createDefaultSalesGoals()
}

const importEmployees = (data: ImportFormat): Employees => {
  if (data.labor) {
    const hourly = data.labor.hourly_employees?.length > 0
      ? data.labor.hourly_employees?.map(x => ({
        id: uniqueId(),
        role: x.Role,
        name: x.Name,
        hourlyWage: parseDubiousNumber(x['Hourly wage']),
        hoursWorkedPerYear: parseDubiousNumber(x['Total hours worked per year']),
        unbillableHoursPerYear: parseDubiousNumber(x['Unbillable hours per year']),
        overtimeHours: parseDubiousNumber(x['Overtime hours']),
        overtimeMultiplier: parseDubiousNumber(x['Overtime multiplier']),
        bonuses: parseDubiousNumber(x.Bonuses),
      }))
      : [emptyHourlyEmployee()]

    const salary = data.labor.salary_employees?.length > 0
      ? data.labor.salary_employees?.map(x => ({
        id: uniqueId(),
        role: x.Role,
        name: x.Name,
        annualSalary: parseDubiousNumber(x['Annual salary']),
        hoursWorkedPerYear: parseDubiousNumber(x['Total hours worked per year']),
        unbillableHoursPerYear: parseDubiousNumber(x['Unbillable hours per year']),
        overtimeHours: parseDubiousNumber(x['Overtime hours']),
        bonuses: parseDubiousNumber(x.Bonuses),
      }))
      : [emptySalaryEmployee()]
    return {
      hourly,
      salary
    }
  }
  return createDefaultEmployees()
}

const importMaterials = (data: ImportFormat): Material[] => {
  if (data.materials) {
    const result = data.materials!.filter(x => x.value !== undefined)
      .map(x => ({ id: uniqueId(), description: x.name, annualCost: parseDubiousNumber(x.value!) }))
    if (result.length > 0) {
      return result
    }
  }
  return createDefaultMaterials()
}

const importSubcontractors = (data: ImportFormat): Subcontractor[] => {
  if (data.subcontractor) {
    const result = data.subcontractor!.filter(x => x.value !== undefined)
      .map(x => ({ id: uniqueId(), description: x.name, annualCost: parseDubiousNumber(x.value!) }))
    if (result.length > 0) {
      return result
    }
  }
  return createDefaultSubcontractors()
}

const importOverhead = (data: ImportFormat): OverheadGroup[] => {
  if (data.overhead) {
    const groups: OverheadGroup[] = []
    let group: OverheadGroup | undefined
    for (const row of data.overhead) {
      if (row.heading) {
        group = { groupName: row.name, overhead: [] }
        groups.push(group)
      } else {
        if (row.name === 'Equip & Trucks Recovered in Overhead') {
          continue
        }
        group?.overhead.push({ id: uniqueId(), description: row.name, annualCost: parseDubiousNumber(row.value!) })
      }
    }

    groups.forEach(x => {
      if (x.overhead.length === 0) {
        x.overhead.push(emptyOverhead())
      }
    })

    return groups
  }

  return createDefaultOverhead()
}

const importEquipment = (data: ImportFormat): EquipmentList => {
  const defaultEquipment = createDefaultEquipment()
  if (data.equipment) {
    const leasedHourly = data.equipment.Leased_Hourly.map((x): LeasedEquipment => ({
      id: uniqueId(),
      name: x.Name,
      quantity: parseDubiousNumber(x.Quantity),
      monthlyPayment: parseDubiousNumber(x['Monthly payment']),
      billableHoursPerYear: parseDubiousNumber(x['Total Billable hours per year']),
      recoveryStrategy: 'BILLABLE'
    }))

    const leasedOverhead = data.equipment.Leased_Overhead.map((x): LeasedEquipment => ({
      id: uniqueId(),
      name: x.Name,
      quantity: parseDubiousNumber(x.Quantity),
      monthlyPayment: parseDubiousNumber(x['Monthly payment']),
      billableHoursPerYear: 0,
      recoveryStrategy: 'OVERHEAD'
    }))

    const ownedHourly = data.equipment.Owned_Hourly.map((x): OwnedEquipment => ({
      id: uniqueId(),
      name: x.Name,
      quantity: parseDubiousNumber(x.Quantity),
      recoveryStrategy: 'BILLABLE',
      billableHoursPerYear: parseDubiousNumber(x['Total Billable hours per year']),
      purchasePrice: parseDubiousNumber(x['Purchase price']),
      yearsUsed: parseDubiousNumber(x['Years used']),
      sellPrice: parseDubiousNumber(x['Sell price'])
    }))

    const ownedOverhead = data.equipment.Owned_Overhead.map((x): OwnedEquipment => ({
      id: uniqueId(),
      name: x.Name,
      quantity: parseDubiousNumber(x.Quantity),
      recoveryStrategy: 'OVERHEAD',
      billableHoursPerYear: 0,
      purchasePrice: parseDubiousNumber(x['Purchase price']),
      yearsUsed: parseDubiousNumber(x['Years used']),
      sellPrice: parseDubiousNumber(x['Sell price'])
    }))

    const misc = data.equipment.Misc_Tools.map((x): MiscEquipment => ({
      id: uniqueId(),
      name: x.Name,
      totalAnnualCost: parseDubiousNumber(x['Total Annual cost'])
    }))

    const equipment = {
      leased: [...leasedOverhead, ...leasedHourly],
      owned: [...ownedOverhead, ...ownedHourly],
      misc
    }

    if (equipment.leased.length < 1) {
      equipment.leased = [...defaultEquipment.leased]
    }

    if (equipment.owned.length < 1) {
      equipment.owned = [...defaultEquipment.owned]
    }

    if (equipment.misc.length < 1) {
      equipment.misc = [...defaultEquipment.misc]
    }

    return equipment
  }

  return defaultEquipment
}

const importSeason = (data: ImportFormat): Season => {
  const defaultSeason = createDefaultSeason()
  if (data.budget) {
    let workingDaysPerWeek = parseDubiousNumber(data.budget.daysInWeek)
    if (workingDaysPerWeek < 1 || workingDaysPerWeek > 7) {
      workingDaysPerWeek = defaultSeason.workingDaysPerWeek
    }

    return {
      startDate: parseDubiousDate(data.budget.startDate, defaultSeason.startDate),
      endDate: parseDubiousDate(data.budget.endDate, defaultSeason.endDate),
      workingDaysPerWeek
    }
  }
  return defaultSeason
}

const importMarkup = (data: ImportFormat): OverheadRecoveryMarkup => {
  if (data.budget) {
    return {
      material: parseDubiousNumber(data.budget.materialRecovery) / 100,
      equipment: parseDubiousNumber(data.budget.equipmentRecovery) / 100,
      subcontractor: parseDubiousNumber(data.budget.subcontractorRecovery) / 100,
    }
  }
  return createDefaultMarkup()
}
